import { useCallback, useRef, useState, useLayoutEffect } from 'react';

export interface IndicatorStyle {
  left: number;
  width: number;
}

export interface UseSegmentedControlAnimationProps {
  currentValue: string;
  transitionDuration: number;
}

export const useSegmentedControlAnimation = ({
  currentValue,
  transitionDuration,
}: UseSegmentedControlAnimationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const updateIndicatorPosition = useCallback(() => {
    if (!containerRef.current || !currentValue) return;

    const container = containerRef.current;
    const activeTrigger = container.querySelector(`[data-state="active"]`) as HTMLElement;

    let timer: NodeJS.Timeout;
    if (activeTrigger) {
      // Use requestAnimationFrame to ensure layout is complete
      const rafId = requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const triggerRect = activeTrigger.getBoundingClientRect();

        const left = triggerRect.left - containerRect.left;
        const width = activeTrigger.offsetWidth;

        setIndicatorStyle({ left, width });

        setIsAnimating(true);
        timer = setTimeout(() => setIsAnimating(false), transitionDuration);
      });

      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timer);
      };
    }
  }, [currentValue, transitionDuration]);

  useLayoutEffect(() => {
    const cleanup = updateIndicatorPosition();

    return cleanup;
  }, [currentValue, updateIndicatorPosition]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    resizeObserverRef.current = new ResizeObserver((entries) => {
      const hasActiveTrigger = entries.some((entry) => entry.target.getAttribute('data-state') === 'active');

      if (hasActiveTrigger) {
        updateIndicatorPosition();
      }
    });

    const triggers = container.querySelectorAll('[role="tab"]');
    triggers.forEach((trigger) => {
      resizeObserverRef.current?.observe(trigger);
    });

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [updateIndicatorPosition]);

  return {
    containerRef,
    indicatorStyle,
    isAnimating,
  };
};
